import { Routes, Route, Navigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Toaster } from '@/components/ui/toaster'
import { ThemeProvider } from '@/components/theme-provider'
import { AuthProvider } from '@/contexts/AuthContext'
import { useAuth } from '@/contexts/AuthContext'
import { startupsApi } from '@/services/api'

// Layout components
import { DashboardLayout } from '@/components/layout/dashboard-layout'
import { AuthLayout } from '@/components/layout/auth-layout'

// Page components
import { LandingPage } from '@/pages/landing/landing'
import { LoginPage } from '@/pages/auth/login'
import { RegisterPage } from '@/pages/auth/register'
import { RegisterInvestorPage } from '@/pages/auth/register-investor'
import { RegisterStartupPage } from '@/pages/auth/register-startup'
import { VerifyEmailPage } from '@/pages/auth/verify-email'
import { DashboardPage } from '@/pages/dashboard/dashboard'
import { ThesisPage } from '@/pages/dashboard/ThesisPage'
import { StartupListPage } from '@/pages/startups/startup-list'
import { StartupDetailPage } from '@/pages/startups/startup-detail'
import { StartupEditPage } from '@/pages/startups/startup-edit'

import { InvestorSubmissionPage } from '@/pages/submission/InvestorSubmission'
import { DropDocsSubmissionPage } from '@/pages/submission/DropDocsSubmission'
import { FormSubmission } from '@/pages/submission/flows/FormSubmission'

import { AnalysisPage } from '@/pages/analysis/analysis'
import { CheatSheet } from '@/pages/analysis/CheatSheet'
import { ProfilePage } from '@/pages/profile/profile'
import { SettingsPage } from '@/pages/settings/settings'
import { NotFoundPage } from '@/pages/not-found'
import { WorkingPage } from '@/pages/working/working'
import { PrivacyPolicyPage } from '@/pages/privacy-policy'
import { TermsOfServicePage } from '@/pages/terms-of-service'
import { AdminDashboard } from '@/pages/admin/AdminDashboard'
import { PlansPage } from '@/pages/plans/plans'



// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth/login" replace />
  }

  if (!user.emailVerified) {
    return <Navigate to="/auth/verify-email" replace />
  }

  // If user doesn't have a role, redirect to registration
  if (!user.role) {
    return <Navigate to="/auth/register" replace state={{ fromGoogleSignIn: true }} />
  }

  return <>{children}</>
}

// Public route wrapper (redirect to dashboard if authenticated with role)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    )
  }

  // Only redirect to dashboard if user has a role
  // Users without a role need to complete registration
  if (user && user.role) {
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />
    }
    if (user.role === 'founder' || (user.role as string) === 'startup') {
      return <FounderRedirect />
    }
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

// Founder redirect component
function FounderRedirect() {
  const { data: startup, isLoading } = useQuery({
    queryKey: ['my-startup-detail'],
    queryFn: () => startupsApi.getMyStartup(),
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    )
  }

  // If founder has a startup, redirect to it
  // @ts-ignore
  if (startup && startup.id) {
    // @ts-ignore
    return <Navigate to={`/startups/${startup.id}`} replace />
  }

  // If no startup found, redirect to startup list (fallback)
  return <Navigate to="/startups" replace />
}

function InvestorRoute({ children }: { children: React.ReactNode }) {
  const { user } = useAuth()

  if (user?.role === 'founder' || (user?.role as string) === 'startup') {
    return <FounderRedirect />
  }

  return <>{children}</>
}

function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isAdmin } = useAuth()

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />
  }

  return <>{children}</>
}

function LandingRoute() {
  const { user, loading } = useAuth()

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (user) {
    // If user has a role, redirect to dashboard
    // If user is a founder/startup, redirect to their startup page
    if (user.role) {
      if (user.role === 'admin') {
        return <Navigate to="/admin" replace />
      }
      if (user.role === 'founder' || (user.role as string) === 'startup') {
        return <FounderRedirect />
      }
      return <Navigate to="/dashboard" replace />
    } else {
      return <Navigate to="/auth/register" replace state={{ fromGoogleSignIn: true }} />
    }
  }

  return <LandingPage />
}

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="minerva-ui-theme">
      <AuthProvider>
        <div className="min-h-screen">
          <Routes>
            {/* Landing page */}
            <Route path="/" element={<LandingRoute />} />
            <Route path="/how-it-works" element={<WorkingPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
            <Route path="/terms-of-service" element={<TermsOfServicePage />} />



            {/* Auth routes */}
            <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
            <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
            <Route
              path="/auth/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/auth/verify-email"
              element={
                <VerifyEmailPage />
              }
            />
            <Route
              path="/auth/*"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <Routes>
                      <Route path="*" element={<Navigate to="/auth/login" replace />} />
                    </Routes>
                  </AuthLayout>
                </PublicRoute>
              }
            />

            {/* Registration Routes (Full Page) */}
            <Route path="/register-investor" element={<PublicRoute><RegisterInvestorPage /></PublicRoute>} />
            <Route path="/register-startup" element={<PublicRoute><RegisterStartupPage /></PublicRoute>} />

            {/* Standalone Protected Routes */}

            {/* Protected routes */}
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <DashboardLayout>
                    <Routes>
                      {/* Dashboard - Only for Investors */}
                      <Route path="dashboard" element={
                        <InvestorRoute>
                          <DashboardPage />
                        </InvestorRoute>
                      } />

                      <Route path="thesis-builder" element={
                        <ThesisPage />
                      } />

                      {/* Startups */}
                      <Route path="startups" element={<StartupListPage />} />
                      <Route path="startups/:id" element={<StartupDetailPage />} />
                      <Route path="startups/:id/edit" element={<StartupEditPage />} />
                      <Route path="startups/:id/analysis" element={<AnalysisPage />} />
                      <Route path="startups/:id/cheat-sheet" element={<CheatSheet />} />

                      {/* Submission Flows */}
                      <Route path="investor-submission" element={<InvestorSubmissionPage />} />
                      <Route path="investor-submission/drop-docs" element={<DropDocsSubmissionPage />} />
                      <Route path="investor-submission/form" element={
                        <div className="min-h-screen bg-slate-50 dark:bg-slate-950 py-12">
                          <FormSubmission onBack={() => window.history.back()} mode="create" />
                        </div>
                      } />

                      {/* User */}
                      <Route path="profile" element={<ProfilePage />} />
                      <Route path="settings" element={<SettingsPage />} />

                      {/* Subscription */}
                      <Route path="plans" element={<PlansPage />} />

                      {/* Admin */}
                      <Route path="admin" element={
                        <AdminRoute>
                          <AdminDashboard />
                        </AdminRoute>
                      } />

                      {/* 404 */}
                      <Route path="*" element={<NotFoundPage />} />
                    </Routes>
                  </DashboardLayout>
                </ProtectedRoute>
              }
            />
          </Routes>

          <Toaster />
        </div>
      </AuthProvider>
    </ThemeProvider >
  )
}

export default App
